import React from "react";
import { FormattedMessage } from "react-intl";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { EnergyLabel, Tupel, getEmissionsklasse } from "components";

import { TVehicle } from "types";

import {
  Autogas,
  Benzin,
  Diesel,
  Elektro,
  MildHybridBenzin,
  MildHybridDiesel,
  PlugInHybridBenzin,
  PlugInHybridDiesel,
  Wasserstoff,
} from "./variants";

import {
  Verbrenner as VerbrennerInformation,
  Elektro as ElektroInformation,
  PlugInHybrid as PlugInHybridInformation,
} from "./information";

import {
  Elektro as ElektroCosts,
  Verbrenner as VerbrennerCosts,
  PlugInHybrid as PlugInHybridCosts,
} from "./costs";

interface IEnergy {
  paperProps?: any;
  vehicle: TVehicle;
}

const Energy: React.FC<IEnergy> = ({
  paperProps = { sx: { p: 2 } },
  vehicle,
}): JSX.Element | null => {
  if (!(vehicle && vehicle.kraftstoff)) return null;

  let Variant;
  let AdditionalInformation = VerbrennerInformation;
  let CostInformation = VerbrennerCosts;
  switch (vehicle.kraftstoff.id) {
    case 1: {
      Variant = Autogas;
      break;
    }
    case 5: {
      Variant = Diesel;
      break;
    }
    case 6: {
      AdditionalInformation = ElektroInformation;
      Variant = Elektro;
      CostInformation = ElektroCosts;
      break;
    }
    case 9: {
      AdditionalInformation = vehicle.plugin_hybrid
        ? PlugInHybridInformation
        : VerbrennerInformation;
      CostInformation = vehicle.plugin_hybrid
        ? PlugInHybridCosts
        : VerbrennerCosts;
      Variant = vehicle.plugin_hybrid ? PlugInHybridBenzin : MildHybridBenzin;
      break;
    }
    case 10: {
      AdditionalInformation = vehicle.plugin_hybrid
        ? PlugInHybridInformation
        : VerbrennerInformation;
      CostInformation = vehicle.plugin_hybrid
        ? PlugInHybridCosts
        : VerbrennerCosts;
      Variant = vehicle.plugin_hybrid ? PlugInHybridDiesel : MildHybridDiesel;
      break;
    }
    case 17: {
      Variant = Wasserstoff;
      break;
    }
    default: {
      Variant = Benzin;
    }
  }

  return (
    <Paper variant="outlined" {...paperProps}>
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        <FormattedMessage
          id="common.verbrauchsangaben"
          defaultMessage="Information über den Energieverbrauch und die CO&#8322;-Emissionen"
        />
      </Typography>
      <Grid container spacing={4} sx={{ typography: "body1" }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Tupel
            force
            labelId="term.marke"
            label="Marke"
            text={vehicle.modell.marke.name}
          />
          <Tupel
            force
            labelId="term.handelsbezeichnung"
            label="Handelsbezeichnung"
            text={vehicle.modell.name}
          />
          <Variant vehicle={vehicle} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <AdditionalInformation vehicle={vehicle} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CO2Class vehicle={vehicle} />
        </Grid>
      </Grid>

      <Box id="costs" sx={{ mt: 2, p: 1, border: 1, typography: "body1" }}>
        <CostInformation vehicle={vehicle} />
      </Box>
    </Paper>
  );
};

interface ICO2Class {
  vehicle: TVehicle;
}

const CO2Class: React.FC<ICO2Class> = ({ vehicle }): JSX.Element | null => {
  if (vehicle.wltp_co2_emissionen === null) return null;
  if (vehicle.aufbau_bereich?.id !== 7) return null;

  return (
    <>
      <Tupel
        force
        labelId="term.co2-klasse"
        label="CO&#8322;-Klasse"
        text={getEmissionsklasse(vehicle.wltp_co2_emissionen)}
      />
      {vehicle.plugin_hybrid && (
        <Tupel
          force
          labelId="term.co2-klasse"
          label="CO&#8322;-Klasse bei entladener Batterie"
          text={getEmissionsklasse(vehicle.wltp_co2_emissionen_entladen)}
        />
      )}
      <EnergyLabel
        svgWidth={300}
        base="A"
        baseWidth={60}
        barHeight={24}
        indicator={getEmissionsklasse(vehicle.wltp_co2_emissionen)}
        indicatorB={
          vehicle.plugin_hybrid
            ? getEmissionsklasse(vehicle.wltp_co2_emissionen_entladen)
            : undefined
        }
      />
      <Typography variant="caption">
        <FormattedMessage
          id="emission.co2.disclaimer"
          defaultMessage="CO&#8322;-Klasse auf der Grundlage der gemessenen CO&#8322;-Emissionen des
      Fahrzeugs ermittelt."
        />
      </Typography>
    </>
  );
};

export default Energy;
